import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  MagnifyingGlass,
  WhatsappLogo,
  ChatCircle,
  CaretDown,
  CaretUp,
  MapPin,
  AddressBook
} from '@phosphor-icons/react';
import { maskPhone, canViewFullPhone } from '../../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsDirectory = ({ onClose }) => {
  const { user } = useAuth();
  const hasFullPhoneAccess = canViewFullPhone(user);

  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Track collapsed state for groups
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedClients, setSelectedClients] = useState({});

  useEffect(() => {
    loadClients();
    const interval = setInterval(loadClients, 5000);
    return () => clearInterval(interval);
  }, []);
  
  const loadClients = async () => {
    try {
      const timestamp = Date.now();
      const response = await axios.get(`${API}/clients/registered`, {
        params: { _t: timestamp }
      });
      setClients(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load clients:', error);
      if (loading) setLoading(false);
    }
  };

  const assignedMBs = Array.isArray(user?.assigned_mbs) ? user.assigned_mbs : ['All'];
  const isSuperAdmin = user?.role === 'super_admin';
  const hasAllMBs = assignedMBs.includes('All');

  const filteredClients = clients.filter(client => {
    if (!isSuperAdmin && !hasAllMBs && assignedMBs.length > 0) {
      const clientMB = client.mb_setup || 'Old Client';
      if (!assignedMBs.includes(clientMB)) return false;
    }

    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.client_name?.toLowerCase().includes(query) ||
      client.username?.toLowerCase().includes(query) ||
      client.mobile_number?.includes(query) ||
      client.phone?.includes(query) ||
      client.serial_number?.toLowerCase().includes(query) ||
      client.client_id?.toLowerCase().includes(query) ||
      client.ifsc_code?.toLowerCase().includes(query) ||
      client.address?.toLowerCase().includes(query) ||
      client.mb_setup?.toLowerCase().includes(query)
    );
  });

  // Group clients by mb_setup
  const groupedClients = filteredClients.reduce((acc, client) => {
    const groupName = client.mb_setup || 'Old Client';
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(client);
    return acc;
  }, {});

  const toggleGroup = (groupName) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClients(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  const toggleSelectAll = (groupName, groupClients) => {
    const allSelected = groupClients.every(c => selectedClients[c.id]);
    const newSelected = { ...selectedClients };
    groupClients.forEach(c => {
      newSelected[c.id] = !allSelected;
    });
    setSelectedClients(newSelected);
  };

  const openChat = async (client) => {
    try {
      let chatType = 'deposit';
      if (user?.role === 'withdrawal_handler') chatType = 'withdrawal';
      else if (user?.role === 'support_handler') chatType = 'support';
      else if (user?.role === 'inquiry_handler') chatType = 'inquiry';

      const res = await axios.post(`${API}/chat/start`, {
        customer_id: client.id,
        chat_type: chatType
      });
      const roomId = res.data?.room_id || res.data?.id;
      if (roomId) {
        const chatPath = user.role === 'super_admin'
          ? `/admin-chat/${roomId}`
          : `/handler-chat/${roomId}`;
        navigate(chatPath);
      } else {
        toast.error('Could not open chat');
      }
    } catch (err) {
      toast.error('Could not open chat');
    }
  };

  const openWhatsApp = (client) => {
    const phone = (client.mobile_number || client.phone || '').replace(/\D/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}`, '_blank');
    } else {
      toast.error('No phone number available');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-[#F3F4F6]" style={{position: 'fixed', inset: 0}}>
      {/* Header */}
      <div className="bg-[#36A055] text-white p-4 flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard');
              }
            }}
            className="hover:bg-green-600 p-2 rounded-full transition-colors"
          >
            <ArrowLeft size={20} weight="bold" />
          </button>
          <div>
            <h1 className="text-xl font-bold leading-tight">Clients Directory</h1>
            <p className="text-xs text-green-100">Admin Control Center</p>
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          
          {/* Card Header & Search */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-2 mb-6">
              <div className="text-[#6C5DD3] bg-[#F1EFFB] p-2 rounded-lg">
                <AddressBook size={24} weight="fill" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Clients Directory</h2>
            </div>

            <div className="relative">
              <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search name, phone, #serial, Client ID, IFSC, city or state..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-300 text-sm"
              />
            </div>
          </div>

          {/* Grouped Lists */}
          <div className="p-4 space-y-4">
            {loading ? (
              <p className="text-center text-gray-500 py-8">Loading directory...</p>
            ) : Object.keys(groupedClients).length === 0 ? (
              <p className="text-center text-gray-500 py-8">No clients found.</p>
            ) : (
              Object.keys(groupedClients).sort().map(groupName => {
                const groupClients = groupedClients[groupName];
                const isCollapsed = collapsedGroups[groupName];
                
                return (
                  <div key={groupName} className="border border-gray-100 rounded-md overflow-hidden bg-[#FAFAFA]">
                    
                    {/* Accordion Header */}
                    <div className="flex items-center justify-between p-3 bg-[#F8F9FA] border-b border-gray-100">
                      <div 
                        className="flex items-center gap-2 cursor-pointer select-none"
                        onClick={() => toggleGroup(groupName)}
                      >
                        <div className="text-gray-500">
                          {isCollapsed ? <CaretDown size={14} weight="bold" /> : <CaretUp size={14} weight="bold" />}
                        </div>
                        <span className="font-semibold text-gray-700 text-sm">{groupName}</span>
                        <span className="bg-[#E6F0FF] text-[#4A88EC] text-[10px] font-bold px-1.5 py-0.5 rounded-sm">
                          {groupClients.length}
                        </span>
                      </div>
                      
                      <button 
                        onClick={() => toggleSelectAll(groupName, groupClients)}
                        className="text-[11px] font-medium text-[#2C7BDE] border border-gray-200 bg-white px-2 py-1 rounded shadow-sm hover:bg-gray-50"
                      >
                        Select All
                      </button>
                    </div>

                    {/* Accordion Body */}
                    {!isCollapsed && (
                      <div className="divide-y divide-gray-100 bg-white">
                        {groupClients.map(client => (
                          <div key={client.id} className="flex items-start p-3 hover:bg-gray-50 transition-colors">
                            {/* Checkbox */}
                            <div className="flex-shrink-0 mr-3 mt-1">
                              <input 
                                type="checkbox" 
                                checked={!!selectedClients[client.id]}
                                onChange={() => toggleClientSelection(client.id)}
                                className="w-4 h-4 border-gray-300 rounded text-[#6C5DD3] focus:ring-[#6C5DD3] cursor-pointer"
                              />
                            </div>
                            
                            {/* Client Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                {client.serial_number && (
                                  <span className="bg-[#F1EFFB] text-[#6C5DD3] text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap">
                                    #{client.serial_number}
                                  </span>
                                )}
                                <span className="font-semibold text-gray-800 text-sm truncate">
                                  {client.client_name || client.username || 'Unknown'}
                                </span>
                              </div>
                              
                              <div className="text-[11px] text-gray-500 flex flex-col gap-0.5 mt-0.5">
                                <div className="flex items-center gap-2">
                                  <span>{maskPhone(client.mobile_number || client.phone, hasFullPhoneAccess) || 'No phone'}</span>
                                  {client.mb_setup && (
                                    <span className="text-[#B5A8F0] text-[9px] font-bold uppercase tracking-wider">{client.mb_setup}</span>
                                  )}
                                </div>
                                
                                {client.address && (
                                  <div className="flex items-center gap-1 text-[9px] text-gray-400 uppercase tracking-wider font-semibold">
                                    <MapPin size={10} weight="fill" className="text-[#6C5DD3] flex-shrink-0" />
                                    <span className="truncate">{client.address}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center gap-3 ml-4 flex-shrink-0 mt-1">
                              <button 
                                onClick={() => openChat(client)}
                                className="text-[#2C7BDE] hover:bg-blue-50 p-1 rounded-full transition-colors"
                                title="Open Chat"
                              >
                                <ChatCircle size={18} weight="fill" />
                              </button>
                              <button 
                                onClick={() => openWhatsApp(client)}
                                className="text-[#25D366] hover:bg-green-50 p-1 rounded-full transition-colors"
                                title="Open WhatsApp"
                              >
                                <WhatsappLogo size={18} weight="fill" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default ClientsDirectory;
